import { EventEmitter } from './utils/eventEmitter';
import { parse } from 'url';
import { RequestError } from './utils/errors';
import { FetchCache } from './utils/cache';

/**
 * Wrapper for fetch
 */
export class Fetch extends EventEmitter {
    constructor(authorizationHeader, endpoint) {
        super();
        this.authorizationHeader = authorizationHeader;
        this.endpoint = endpoint;

        this.language = null;

        // Each fetcher has its own cache
        this.cache = new FetchCache();
    }

    initialize(endpoint) {
        this.endpoint = endpoint;
    }

    setToken(token) {
        this.authorizationHeader = `Bearer ${token}`;
    }

    setAuthorizationHeader(authorizationHeader) {
        this.authorizationHeader = authorizationHeader;
    }

    setLanguage(language){
        this.language = language;
    }

    set_in_cache({ url, headers, timeout, value }) {
        return this.cache.set_request({ url, headers, timeout, value });
    }

    get_from_cache({ url, headers }) {
        return this.cache.get_request({ url, headers });
    }

    clear_cache(url) {
        // small hack to fix auto-generated links
        // and since we can make requests to any endpoint
        // we must append the correct endpoint
        if(!url.startsWith('http')) {
            url = this.endpoint + url;
        }

        if(!url) {
            return this.cache.clear();
        }

        return this.cache.remove_request({url});
    }

    parse_response(response) {
        const contentType = response.headers.get('Content-type') || '';
        if (contentType.indexOf('application/json') > -1) {
            return response.json();
        }
        const contentDisposition = response.headers.get('Content-Disposition') || '';
        if (
            contentDisposition.indexOf('attachment') > -1
        ) {
            return response.blob();
        }
        return response.text();
    }

    handle_response(response) {
        return this.parse_response(response).then(data => {
            return this.format_response(response, data);
        });
    }

    format_response(response, data) {
        return {
            // Kept for retrocomp
            ok: true,
            json: () => Promise.resolve(data),
            response: data,
            headers: response.headers,
            status: response.status,
            raw_response: response
        };
    }

    validate_response(url, response, metadata) {
        // XXX: we could handle 300 responses now on the same endpoint
        if (response.status < 200 || response.status > 299) {
            // Emit error event to be caught somewhere
            this.emit(response.status.toString(), { response, text: response.response, headers: response.headers, metadata });
            throw new RequestError('[Fetch] Error requesting resource', url, response.status, response.response, response.headers);
        }
    }

    request(path, options) {
        if (options === undefined) {
            if (typeof path === 'string') {
                options = {};
            } else {
                // request({ path, .. }) => request(path, { .. })
                options = path;
                path = options.path;
            }
        }

        const {
            endpoint = this.endpoint,
            method = 'GET',
            query = {},
            auth = true,
            body,
            allow_cache = true, // get from cache
            cache = true, // set in cache,
            cache_timeout = 30,
            metadata = null
        } = options;

        const headers = new Headers(options.headers || {});

        let querystring = null;
        if (query instanceof Object && Object.keys(query).length) {
            querystring = Object.entries(query)
                // Ignore values that do nothing in query params
                .filter(([, value]) => ![null, undefined, ''].includes(value))
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join('&');
        } else if (typeof query === 'string') {
            querystring = query;
        }

        if (querystring) {
            path = path.indexOf('?') > -1 ? `${path}&${querystring}` : `${path}?${querystring}`;
        }

        // prefix the path with the endpoint unless we have an absolute URL
        let url = parse(path).host ? path : `${endpoint}${path}`;

        let data = options.data;
        if (data && !(data instanceof window.FormData) && !body) {
            if (!headers.get('Content-Type')) {
                headers.set('Content-Type', 'application/json; charset=utf-8');
            }

            if (data && data instanceof Object) {
                data = JSON.stringify(data);
            }
        }

        if(!headers.get('Accept-Language') && this.language) {
            headers.set('Accept-Language', this.language);
        }

        if (this.authorizationHeader && auth && !headers.get('Authorization')) {
            headers.set('Authorization', this.authorizationHeader);
        }

        if (!headers.get('Accept')) {
            headers.set('Accept', 'application/json');
        }

        const cached = this.get_from_cache({ url, headers });
        if(method.toUpperCase() === 'GET' && allow_cache && cached !== undefined) {
            return new Promise((resolve, reject) => {
                try {
                    this.validate_response(url, cached, metadata);
                    return resolve(cached);
                } catch(e) {
                    reject(e);
                }
            });
        }


        let weezFetchStartedAt = new Date();
        return fetch(url, {
            method,
            body: body || data,
            headers
        }).then(response => {
           return this.handle_response(response);
        }).then(formatted_response => {
            if(cache && method.toUpperCase() === 'GET') {
                // If a GET receives a proper server error (like 404, 500, 403...), that
                // answer might get cached. POST and PUT request won't be cached, so they
                // will be performed each time. Network error won't even make it to here,
                // they are thrown by the fetch call earlier, so they won't be cached either.
                this.set_in_cache({
                    url,
                    headers,
                    timeout: cache_timeout,
                    value: formatted_response
                });
            }

            // We throw the error here to keep some errors in cache
            this.validate_response(url, formatted_response, metadata);
            return formatted_response;
        }).catch(error=>{
            // `error` here might be a RequestError if we received a proper error from the API.
            // It could also be a `TypeError: NetworkError when attempting to fetch resource.`.
            // Either way, we provide timing info for the app downstream to interpret and report
            // the error to the user as it sees fit. It is useful because some of our 503 are
            // lacking CORS headers and are reported as network errors to the front end code.
            error.weezFetchStartedAt = weezFetchStartedAt;
            throw error
        })
    }

    get(path, options) {
        return this.request(path, {
            ...options,
            method: 'GET'
        });
    }

    post(path, options) {
        return this.request(path, {
            ...options,
            method: 'POST'
        });
    }

    put(path, options) {
        return this.request(path, {
            ...options,
            method: 'PUT'
        });
    }

    patch(path, options) {
        return this.request(path, {
            ...options,
            method: 'PATCH'
        });
    }

    delete(path, options) {
        return this.request(path, {
            ...options,
            method: 'DELETE'
        });
    }
}

export const fetcher = new Fetch();
